import React, { FC, useMemo, useState } from "react"
import { toast } from "react-toastify"
import clsx from "clsx"

import { balanceService } from "services/balance/balance.service"

type Props = {
  open: boolean
  onClose: () => void
  onCreated: () => void
  loading: boolean
  setLoading: (v: boolean) => void
}

export const CreatePaymentRouteModal: FC<Props> = ({ open, onClose, onCreated, loading, setLoading }) => {
  const [processor, setProcessor] = useState("")
  const [paymentMethod, setPaymentMethod] = useState("")
  const [terminalName, setTerminalName] = useState("")
  const [isActive, setIsActive] = useState(true)

  const canSubmit = useMemo(() => {
    return processor.trim() && paymentMethod.trim() && terminalName.trim()
  }, [processor, paymentMethod, terminalName])

  if (!open) return null

  const onSubmit = async () => {
    if (!canSubmit) {
      toast.error("Please fill processor, method and terminal")
      return
    }

    setLoading(true)
    try {
      const res = await balanceService.createPaymentRoute({
        processor: processor.trim(),
        payment_method: paymentMethod.trim(),
        terminal_name: terminalName.trim(),
        is_active: isActive
      })
      if (!res) return
      onCreated()
      setProcessor("")
      setPaymentMethod("")
      setTerminalName("")
      setIsActive(true)
    } finally {
      setLoading(false)
    }
  }

  const input =
    "w-full rounded-lg border px-3 py-2 text-sm outline-none transition " +
    "bg-white dark:bg-[#0C0C1A] " +
    "border-[rgba(139,92,246,0.4)] focus:border-[rgba(139,92,246,1)] " +
    "text-[rgba(6,7,21,1)] dark:text-white"

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div
        className={clsx(
          "relative w-full max-w-[520px] rounded-2xl border p-4 shadow-xl",
          "bg-white dark:bg-[#0C0C1A]",
          "border-[rgba(0,0,0,0.06)] dark:border-[rgba(255,255,255,0.1)]"
        )}
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-base font-semibold text-[rgba(6,7,21,1)] dark:text-white">Create payment route</div>
            <div className="mt-1 text-sm text-[rgba(6,7,21,0.7)] dark:text-white/70">
              Adds a MANUAL route for fee configuration even if it hasn’t been seen yet.
            </div>
          </div>

          <button
            className={clsx(
              "rounded-lg px-3 py-2 text-sm font-semibold transition",
              "bg-[rgba(0,0,0,0.06)] text-[rgba(6,7,21,0.7)] dark:bg-white/10 dark:text-white/70",
              loading && "opacity-60 cursor-not-allowed"
            )}
            onClick={onClose}
            disabled={loading}
          >
            Close
          </button>
        </div>

        <div className="mt-4 space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">Processor</div>
            <input
              className={input}
              value={processor}
              onChange={(e) => setProcessor(e.target.value)}
              disabled={loading}
              placeholder="BAZPAY"
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">Method</div>
            <input
              className={input}
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              disabled={loading}
              placeholder="BASIC_CARD"
            />
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[rgba(6,7,21,0.6)] dark:text-white/70">Terminal</div>
            <input
              className={input}
              value={terminalName}
              onChange={(e) => setTerminalName(e.target.value)}
              disabled={loading}
              placeholder="TrustedTerminal"
            />
          </div>

          <label className="flex items-center gap-2 pt-1">
            <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} disabled={loading} />
            <span className="text-sm text-[rgba(6,7,21,0.8)] dark:text-white/80">Active</span>
          </label>
        </div>

        <div className="mt-5 flex items-center justify-end gap-2">
          <button
            className={clsx(
              "rounded-lg px-4 py-2 text-sm font-semibold transition",
              "bg-[rgba(0,0,0,0.06)] text-[rgba(6,7,21,0.7)] dark:bg-white/10 dark:text-white/70",
              loading && "opacity-60 cursor-not-allowed"
            )}
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            className={clsx(
              "rounded-lg px-4 py-2 text-sm font-semibold transition",
              canSubmit
                ? "bg-[rgba(139,92,246,1)] text-white hover:bg-[rgba(139,92,246,0.9)]"
                : "bg-[rgba(0,0,0,0.06)] text-[rgba(6,7,21,0.35)] dark:bg-white/10 dark:text-white/30",
              loading && "opacity-60 cursor-not-allowed"
            )}
            onClick={onSubmit}
            disabled={loading || !canSubmit}
            title={canSubmit ? "Create route" : "Fill all fields"}
          >
            Create
          </button>
        </div>
      </div>
    </div>
  )
}
